import { useEffect, useId, useRef, useState } from 'react'
import { useViewPreferences } from '../context/ViewPreferencesContext'
import { VIEW_PREFERENCE_OPTIONS } from '../context/viewPreferencesStore'

const DISPLAY_FIELDS = Object.freeze([
  {
    key: 'interfaceScale',
    label: 'Interface size',
    description: 'Scales text, controls, and spacing across Operations.',
  },
  {
    key: 'density',
    label: 'Density',
    description: 'Adjusts page whitespace, cards, and table row height.',
  },
  {
    key: 'contentWidth',
    label: 'Content width',
    description: 'Sets the maximum working width of the page content.',
  },
  {
    key: 'tableMode',
    label: 'Adaptive lists',
    description: 'Overrides shared adaptive lists. Bespoke grids keep their native layout.',
  },
])

export function DisplayPreferenceFields({ idPrefix = 'display-preference', showDescriptions = false }) {
  const { preferences, setViewPreference } = useViewPreferences()

  return (
    <div className="display-preferences-grid">
      {DISPLAY_FIELDS.map((field) => {
        const inputId = `${idPrefix}-${field.key}`
        const descriptionId = `${inputId}-description`
        return (
          <label className="display-preference-field" htmlFor={inputId} key={field.key}>
            <span className="display-preference-field__label">{field.label}</span>
            <select
              id={inputId}
              value={preferences[field.key]}
              onChange={(event) => setViewPreference(field.key, event.target.value)}
              aria-describedby={showDescriptions ? descriptionId : undefined}
            >
              {VIEW_PREFERENCE_OPTIONS[field.key].map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {showDescriptions && (
              <small id={descriptionId}>{field.description}</small>
            )}
          </label>
        )
      })}
    </div>
  )
}

export default function DisplayControls({ className = '' }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return undefined

    containerRef.current?.querySelector('select')?.focus()

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div className={`display-controls${className ? ` ${className}` : ''}`} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="display-controls__trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        Display
      </button>
      {open && (
        <section className="display-controls__panel" role="dialog" aria-labelledby={titleId}>
          <header>
            <div>
              <strong id={titleId}>Display settings</strong>
              <small>Saved in this browser</small>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                triggerRef.current?.focus()
              }}
              aria-label="Close display settings"
            >
              ×
            </button>
          </header>
          <DisplayPreferenceFields idPrefix="display-menu" />
        </section>
      )}
    </div>
  )
}
